// export default CheckoutForm;
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useStripe, useElements, PaymentElement, AddressElement } from '@stripe/react-stripe-js';
import { makeStyles } from '@material-ui/core/styles';
import PromoCode from '../PromoCode';
import { useSelector } from 'react-redux';
import { getBillingInfoSelector } from '../BillingInformationView/selectors';
import { Alert, AlertTitle } from '@material-ui/lab';
import { normalizeBillingInfo } from '../../utils/pepperUtils';
import { getStripeErrorSelector } from './selectors';
import { ExpressCheckoutElement } from '@stripe/react-stripe-js';
import PaymentDetailsPayPal from '../PaymentDetails/PaymentDetailsPayPal';
import { StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';

const useStyles = makeStyles(theme => ({
  form: {
    padding: '20px',
    margin: '20px 0',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  },
  button: {
    backgroundColor: '#635BFF',
    color: 'white',
    padding: '14px 24px',
    marginTop: '24px',
    fontWeight: 600,
    borderRadius: '8px',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#524DDB',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      backgroundColor: '#A5A5A5',
      cursor: 'not-allowed',
    },
  },
  errorAlert: {
    marginTop: '16px',
    '& .MuiAlert-message': {
      width: '100%',
    },
  },
  errorList: {
    margin: '8px 0 0 16px',
    padding: 0,
  },
  expressCheckoutContainer: {
    borderRadius: '12px',
    padding: '24px',
    margin: '20px 0',
    position: 'relative',
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  },
  expressCheckoutLabel: {
    position: 'absolute',
    top: '-6px',
    left: '16px',
    padding: '0 8px',
    fontWeight: 600,
    color: '#333',
    fontSize: '14px',
  },
  expressCheckoutButtons: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '8px',
    alignItems: 'center',
    [theme.breakpoints.down(900)]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  expressCheckoutLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '88px',
  },
}));

interface Props {
  brand: string;
  showPromoSection?: boolean;
  userInfo?: any;
  onComplete: (billingInfo: any) => void;
  ref: React.RefObject<any>;
  lastPath: string;
  updateErrorMapping: (errorMapping: any) => void;
  updateBillingInfo: (billingInfo: any) => void;
}

const getUserFriendlyError = (error: any): string => {
  // check if error is a string
  if (typeof error === 'string') {
    return error;
  }

  // Common payment errors
  if (error?.type === 'card_error') {
    switch (error?.code) {
      case 'card_declined':
        return 'Your card was declined. Please try another card.';
      case 'expired_card':
        return 'Your card has expired. Please use a different card.';
      case 'incorrect_cvc':
        return 'The security code (CVC) is incorrect. Please check and try again.';
      case 'invalid_number':
        return 'The card number is invalid. Please check and try again.';
      default:
        return 'There was an issue with your card. Please try again or use a different card.';
    }
  }

  // Validation errors
  if (error?.type === 'validation_error') {
    return 'Please check your payment information and try again.';
  }

  // Generic errors
  if (error?.message?.includes('confirmation token')) {
    return "We couldn't process your payment at this time. Please try again later.";
  }

  // Default error
  return 'Something went wrong. Please try again later or contact support if the problem persists.';
};

const CheckoutForm = forwardRef(
  ({ brand, showPromoSection, userInfo, onComplete, lastPath, updateErrorMapping, updateBillingInfo }: Props, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const billingInfoFromStore = useSelector(getBillingInfoSelector);
    const normalizedBillingInfo = normalizeBillingInfo(billingInfoFromStore);
    const classes = useStyles();
    const [errors, setErrors] = useState<string[]>([]);
    const recurlyError = useSelector(getStripeErrorSelector);

    // Handle Redux store errors (like the 500 error from the test card)
    useEffect(() => {
      if (recurlyError) {
        const userFriendlyMessage = getUserFriendlyError(recurlyError);
        setErrors([userFriendlyMessage]);
      }
    }, [recurlyError]);

    const handleError = (error: any) => {
      const userFriendlyMessage = getUserFriendlyError(error);
      setErrors([userFriendlyMessage]);
    };

    const submit = async () => {
      setErrors([]);

      if (!stripe || !elements) {
        setErrors(['The payment system is temporarily unavailable. Please refresh the page and try again.']);
        return;
      }

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          handleError(submitError);
          throw submitError;
        }

        const currentOrigin = window.location.origin;
        const returnUrl = `${currentOrigin}/subscriptions`;

        const tokenResponse = await stripe.createConfirmationToken({
          elements,
          params: {
            return_url: returnUrl,
          },
        });

        if (tokenResponse.error) {
          handleError(tokenResponse.error);
          throw tokenResponse.error;
        }

        const confirmationToken = tokenResponse.confirmationToken?.id;
        if (!confirmationToken) {
          throw new Error('payment_processing_error');
        }

        const billingInfo = {
          token: tokenResponse.confirmationToken.id,
          first_name: tokenResponse.confirmationToken.payment_method_preview.billing_details.name?.split(' ')[0] || '',
          last_name: tokenResponse.confirmationToken.payment_method_preview.billing_details.name?.split(' ')[1] || '',
          address1: tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.line1 || '',
          address2: tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.line2 || '',
          city: tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.city || '',
          state: tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.state || '',
          postal_code:
            tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.postal_code || '',
          country: tokenResponse.confirmationToken.payment_method_preview.billing_details.address?.country || '',
          cardNumberLast4: tokenResponse.confirmationToken.payment_method_preview.card?.last4 || '',
          brand: tokenResponse.confirmationToken.payment_method_preview.card?.brand || '',
        };

        onComplete(billingInfo);
      } catch (err: any) {
        handleError(err);
        throw err;
      }
    };

    useImperativeHandle(ref, () => ({
      submit,
    }));

    const expressOptions: StripeExpressCheckoutElementOptions = {
      layout: { maxColumns: 3, overflow: 'never' }, // 2-wide "card"
      paymentMethodOrder: ['googlePay', 'applePay', 'link'],
      buttonHeight: 44,
    };

    return (
      <>
        <div className={classes.expressCheckoutContainer}>
          <div className={classes.expressCheckoutLabel}>Express Checkout</div>

          <div className={classes.expressCheckoutButtons}>
            <ExpressCheckoutElement id="express-checkout" options={expressOptions} onConfirm={submit} />
            <PaymentDetailsPayPal
              brand={brand}
              lastPath={lastPath}
              userInfo={userInfo}
              updateErrorMapping={updateErrorMapping}
              updateBillingInfo={updateBillingInfo}
            />
          </div>
        </div>
        <form id="payment-form" className={classes.form}>
          {errors.length > 0 && (
            <Alert
              style={{ marginBottom: 10 }}
              severity="error"
              className={classes.errorAlert}
              onClose={() => setErrors([])}>
              <AlertTitle>Error</AlertTitle>
              {errors.length === 1 ? (
                errors[0]
              ) : (
                <ul className={classes.errorList}>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
          <AddressElement
            id="address-element"
            options={{
              mode: 'billing',
              defaultValues: {
                name:
                  normalizedBillingInfo?.first_name && normalizedBillingInfo?.last_name
                    ? normalizedBillingInfo?.first_name + ' ' + normalizedBillingInfo?.last_name
                    : '',
                address: {
                  line1: normalizedBillingInfo?.address1 || '',
                  line2: normalizedBillingInfo?.address2 || '',
                  city: normalizedBillingInfo?.city || '',
                  state: normalizedBillingInfo?.state || '',
                  postal_code: normalizedBillingInfo?.zip || '',
                  country: normalizedBillingInfo?.country || '',
                },
              },
            }}
          />
          <PaymentElement
            id="payment-element"
            options={{
              fields: {
                billingDetails: {
                  name: 'auto',
                  address: {
                    line1: 'auto',
                    line2: 'auto',
                    city: 'auto',
                    state: 'auto',
                    postalCode: 'auto',
                    country: 'auto',
                  },
                },
              },
              wallets: { applePay: 'auto', googlePay: 'auto' },
              layout: 'accordion',
              paymentMethodOrder: ['googlePay', 'applePay', 'card'], // Prioritize Google Pay
            }}
          />
          {showPromoSection && (
            <div style={{ marginTop: 25 }}>
              <PromoCode brand={brand} />
            </div>
          )}
        </form>
      </>
    );
  },
);

export default CheckoutForm;
